<script setup lang="ts">
import type { Form, FormSubmitEvent, RadioGroupItem } from '#ui/types';
import { useMutation } from '@tanstack/vue-query';
import { z } from 'zod';

import {
    apiSendOtp,
    apiVerifyOtp,
    BackendErrorCode,
    type BackendErrorEntity,
    type BodyType,
    type ErrorType,
    OtpChannel,
    type SendUserOtpBodyDto,
    type VerifyUserOtpBodyDto,
} from '~/apiClient';
import { AUTOCOMPLETE_OFF } from '~/constants/form';

const DEFAULT_OTP_CHANNEL = OtpChannel.sms;

interface VerifyOtpFormProps {
    email: string;
    requestOtp?: boolean;
}

// PROPS
const { email, requestOtp } = defineProps<VerifyOtpFormProps>();

// COMPOSABLES
const { getToken, formatRequestOptions } = useInvisibleRecaptcha();
const { tt } = useTypedI18n();
const { logError } = useLogs();
const { notifyBackendError, notifyGenericError, notifyError } = useNotification();
const { mutateAsync: mutateVerify } = useMutation<
    Awaited<ReturnType<typeof apiVerifyOtp>>,
    ErrorType<BackendErrorEntity>,
    { data: BodyType<VerifyUserOtpBodyDto>; token: string | null },
    unknown
>({
    mutationFn: ({ data, token }) => apiVerifyOtp(data, formatRequestOptions(token)),
});
const { mutateAsync: mutateSend, isPending: isPendingSend } = useMutation<
    Awaited<ReturnType<typeof apiSendOtp>>,
    ErrorType<BackendErrorEntity>,
    { data: BodyType<SendUserOtpBodyDto>; token: string | null },
    unknown
>({
    mutationFn: ({ data, token }) => apiSendOtp(data, formatRequestOptions(token)),
});
const { data: session, getSession, state: authState } = useAuthUtils();
const { trackSignupSuccess } = useTracking();

// EMITS
const emit = defineEmits(['success']);

// DATA
const isPendingVerify = ref<boolean>(false);
const isSubmitting = computed(() => isPendingVerify.value || isPendingSend.value);

const formSchema = z.object({
    email: z.email(tt('validation.msg.invalid', { field: tt('login.email') })),
    otp: z
        .array(z.string().length(1, tt('validation.msg.required', { field: tt('signup.verify.otp') })))
        .length(6, tt('validation.msg.invalid', { field: tt('signup.verify.otp') })),
});
type FormSchema = z.infer<typeof formSchema>;
const formData = reactive<FormSchema>({
    email,
    otp: ['', '', '', '', '', ''],
});
const form = useTemplateRef<Form<FormSchema>>('form');

const channelItems = ref<RadioGroupItem[]>([
    {
        label: tt('signup.verify.resend.sms'),
        value: OtpChannel.sms,
    },
    {
        label: tt('signup.verify.resend.email'),
        value: OtpChannel.email,
    },
]);
const channelModel = ref<OtpChannel>(DEFAULT_OTP_CHANNEL);
const channelRequested = ref<OtpChannel>(channelModel.value);

const resendCooldown = ref(0);
const isResendDisabled = computed(() => resendCooldown.value > 0);
let resendTimer: ReturnType<typeof setInterval> | undefined = undefined;

// HANDLERS
const onSubmit = async (event: FormSubmitEvent<FormSchema>): Promise<void> => {
    let recaptchaToken: string | null = null;
    isPendingVerify.value = true;

    try {
        recaptchaToken = await getToken();
    } catch {
        notifyError({
            title: tt('validation.msg.recaptcha'),
            description: tt('notifications.tryAgain'),
        });

        return;
    }

    await mutateVerify({
        data: {
            email,
            otp: event.data.otp.join(''),
            channel: channelRequested.value,
        },
        token: recaptchaToken,
    })
        .then(async ({ token }) => {
            authState.setToken(token);
            await getSession();
            session.value?.user && trackSignupSuccess(session.value.user);
            emit('success');
        })
        .catch((apiError: ErrorType<BackendErrorEntity>) => {
            logError('Error verifying account with OTP', { error: apiError, email, channel: channelModel.value });

            if (isFetchError(apiError) && isBackendError(apiError.data)) {
                if ([BackendErrorCode.OTPCodeNotValid, BackendErrorCode.OtpExpiredOrNotFound].includes(apiError.data.error.code)) {
                    form.value?.setErrors([{ name: 'otp', message: tt('validation.msg.invalid', { field: tt('signup.verify.otp') }) }]);
                }

                if (apiError.data.error.code === BackendErrorCode.OtpResendTooSoon) {
                    startResendCooldown();
                }

                notifyBackendError({ error: apiError.data.error });

                return;
            }

            notifyGenericError();
        });

    isPendingVerify.value = false;
};

const startResendCooldown = (): void => {
    if (resendTimer) clearInterval(resendTimer);

    resendCooldown.value = 60;
    resendTimer = setInterval(() => {
        resendCooldown.value--;

        if (resendCooldown.value <= 0) {
            clearInterval(resendTimer);
        }
    }, 1000);
};

const onResend = async (): Promise<void> => {
    let recaptchaToken: string | null = null;

    try {
        recaptchaToken = await getToken();
    } catch {
        notifyError({
            title: tt('validation.msg.recaptcha'),
            description: tt('notifications.tryAgain'),
        });

        return;
    }

    await mutateSend({ data: { email, channel: channelModel.value }, token: recaptchaToken })
        .then(() => {
            channelRequested.value = channelModel.value;
            formData.otp = ['', '', '', '', '', ''];
            form.value?.clear();
            startResendCooldown();
        })
        .catch((apiError: ErrorType<BackendErrorEntity>) => {
            logError('Error requesting new OTP', { error: apiError, email, channel: channelModel.value });

            if (!apiError.data?.error) {
                notifyGenericError();

                return;
            }

            const { error } = apiError.data;
            notifyBackendError({ error });
        });
};

// LIFECYCLE
onMounted(() => {
    if (requestOtp) {
        onResend();
    } else {
        startResendCooldown();
    }
});

onUnmounted(() => {
    clearInterval(resendTimer);
});
</script>

<template>
    <UForm ref="form" class="flex w-full flex-col gap-4" :state="formData" :schema="formSchema" @submit="onSubmit">
        <p class="text-muted text-center font-medium">
            {{
                tt('signup.verify.subtitle', {
                    channel: channelRequested === OtpChannel.email ? tt('signup.verify.resend.email') : tt('signup.verify.resend.sms'),
                })
            }}
        </p>

        <UFormField :label="tt('signup.fields.email.base')" name="email" required>
            <UInput v-model.trim="formData.email" disabled />
        </UFormField>
        <UFormField :label="tt('signup.verify.otp')" name="otp" required>
            <UPinInput v-model="formData.otp" :disabled="isSubmitting" :autocomplete="AUTOCOMPLETE_OFF" :length="6" otp />
        </UFormField>
        <UButton :label="tt('signup.verify.confirmCta')" type="submit" size="xl" :disabled="isPendingSend" :loading="isPendingVerify" block />

        <USeparator />

        <p class="text-muted text-center font-medium">{{ tt('signup.verify.resend.description') }}</p>
        <URadioGroup v-model="channelModel" :items="channelItems" :disabled="isSubmitting" />
        <UButton
            color="neutral"
            variant="outline"
            :label="tt('signup.verify.resend.cta')"
            size="xl"
            block
            :disabled="isResendDisabled || isPendingVerify"
            :loading="isPendingSend"
            @click="onResend"
        >
            <template v-if="resendCooldown" #trailing>{{ resendCooldown }}</template>
        </UButton>
    </UForm>
</template>
